import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCan } from "../auth/use-can";
import type { TreatmentPlan } from "@dentacore/shared-types";
import { isPlanOpen } from "@dentacore/shared-types";

import { linkPart, listPlans, unlinkPart } from "../api/plans";
import { Select } from "../ui/select";
import type { SelectOption } from "../ui/select";
import styles from "./plan-link.module.css";
import { useBookingScoped } from "./booking-location";

/**
 * The treatment plan this appointment is part of — read, attached, detached.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 *
 * A plan's visits could only ever be booked *from the plan*, which creates the
 * appointment. That is the right flow when somebody is looking at a plan and it
 * was the **only** flow, so from the calendar — where most booking actually
 * happens — a plan was unreachable. The client's report: *"i have no way of
 * editing the selected appointment to add the treatment plan for the patient,
 * the treatment plan workflow is broken."*
 *
 * Asked which direction he wanted, he chose this one: name the plan from the
 * appointment, and leave the Plans screen as it is. Decision D6.
 *
 * ── What it offers, and what it refuses to offer ────────────────────────────
 *
 * **Only visits that are actually next.** A plan's visits are booked strictly
 * in order (TP8) and the API refuses out of turn, so offering every visit would
 * be offering refusals. The list is each in-progress plan's first visit that is
 * not yet complete.
 *
 * **Nothing at all for a held chair**, which has no patient and so no plan.
 *
 * ── A failed read is silent ─────────────────────────────────────────────────
 *
 * This is an addition to a panel whose job is the appointment. If plans cannot
 * be read the rest of the panel still works, and a red band about treatment
 * plans over a booking somebody is trying to check in would be noise.
 */
export function PlanLink({
  appointmentId,
  patientId,
  timezone,
  onChanged,
  onOpenAppointment,
}: {
  appointmentId: string;
  /** Null for a buffer block or a held chair — this draws nothing then. */
  patientId: string | null;
  /** The branch's zone, for the chain's dates (P2 Q8 — never the browser's). */
  timezone: string;
  /** The panel re-reads the appointment, so a linked plan shows on its rows. */
  onChanged?: () => void;
  /** Open another visit of the chain on the calendar (CH7). */
  onOpenAppointment?: (appointmentId: string, startsAt: string) => void;
}): React.JSX.Element | null {
  const here = useBookingScoped();
  /** Linking a visit to a plan, or unlinking it, is `plans.edit` (QA third run). */
  const edits = useCan()("plans.edit");
  const [plans, setPlans] = useState<TreatmentPlan[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(
    (signal?: AbortSignal) => {
      if (patientId === null) return;
      here(listPlans)(patientId, signal)
        .then(setPlans)
        .catch(() => {
          /* Silent — see above. */
        });
    },
    [patientId],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  /**
   * The visit already attached to *this* appointment, if any.
   *
   * Found by walking the plans rather than read off the appointment, because
   * the link lives on the plan's steps — an appointment does not know its plan,
   * which is exactly the asymmetry that made this unreachable from here.
   */
  const attached = useMemo(() => {
    for (const plan of plans ?? []) {
      for (const [index, part] of plan.parts.entries()) {
        if (part.items.some((item) => item.appointmentId === appointmentId)) {
          return { plan, part, number: index + 1 };
        }
      }
    }
    return null;
  }, [plans, appointmentId]);

  /**
   * Each in-progress plan's next unfinished visit — the only ones the API will
   * accept, so the only ones worth offering.
   */
  const options = useMemo<SelectOption[]>(() => {
    const rows: SelectOption[] = [];

    for (const plan of plans ?? []) {
      /**
       * **Open means not finished and not abandoned** (TP29, TP31).
       *
       * This used to read `status !== "IN_PROGRESS"`, against a column nothing
       * ever advanced — so it matched every plan ever created, and a patient
       * whose crown was finished last month was still offered it here.
       */
      if (!isPlanOpen(plan)) continue;

      const index = plan.parts.findIndex(
        (part) => !part.items.every((item) => item.status === "COMPLETED"),
      );
      if (index === -1) continue;

      const part = plan.parts[index];
      if (part === undefined) continue;

      rows.push({
        value: `${plan.id}:${part.id}`,
        label: `${plan.title} — visit ${String(index + 1)}`,
        keywords: part.items.map((item) => item.label).join(" "),
        hint: part.title ?? undefined,
      });
    }

    return rows;
  }, [plans]);

  if (patientId === null) return null;

  // Nothing attached and nothing attachable is not a section worth drawing —
  // most bookings are a single procedure and no plan is involved (TP4).
  if (attached === null && (options.length === 0 || !edits)) return null;

  async function attach(value: string): Promise<void> {
    const [planId, partId] = value.split(":");
    if (planId === undefined || partId === undefined) return;

    setBusy(true);
    setError(null);
    try {
      await here(linkPart)(planId, partId, appointmentId);
      reload();
      onChanged?.();
    } catch (cause) {
      // The server's own sentence: the order rule names which visit is
      // outstanding, and paraphrasing it would lose the number.
      setError(cause instanceof Error ? cause.message : "Could not attach that plan.");
    } finally {
      setBusy(false);
    }
  }

  async function detach(): Promise<void> {
    if (attached === null) return;

    setBusy(true);
    setError(null);
    try {
      await here(unlinkPart)(attached.plan.id, attached.part.id);
      reload();
      onChanged?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not detach that plan.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.block} data-testid="panel-plan">
      <h3 className={styles.title}>Treatment plan</h3>

      {attached === null ? (
        <Select
          id={`plan-link-${appointmentId}`}
          label="Part of a plan?"
          value=""
          options={options}
          onChange={(value) => void attach(value)}
          testId="panel-plan-select"
          placeholder="Not part of a plan"
          disabled={busy}
        />
      ) : (
        <div className={styles.attached}>
          <Link
            to={`/plans/${attached.plan.id}`}
            className={styles.planName}
            data-testid="panel-plan-name"
          >
            {attached.plan.title}
          </Link>
          <span className={styles.visit}>
            Visit {attached.number}
            {attached.part.title === null ? "" : ` · ${attached.part.title}`}
          </span>
          <ul className={styles.steps}>
            {attached.part.items.map((item) => (
              <li key={item.id}>{item.label}</li>
            ))}
          </ul>

          {/*
            **The whole chain** (CH7): every visit of the plan with its date,
            this one marked, and each booked one a way to it on the calendar.
            Only when there is more than one visit — a chain of one is a visit.
          */}
          {attached.plan.parts.length > 1 && (
            <ol className={styles.chain} data-testid="panel-chain">
              {attached.plan.parts.map((part, index) => {
                const isThis = part.id === attached.part.id;
                const when =
                  part.completedAt !== null
                    ? "Done"
                    : part.booking === null
                      ? "No date yet"
                      : formatWhen(part.booking.startsAt, timezone);
                const label = `Visit ${String(index + 1)}${
                  part.title === null || part.title === "" ? "" : ` · ${part.title}`
                }`;
                return (
                  <li key={part.id} className={isThis ? styles.chainThis : styles.chainVisit}>
                    {part.booking !== null && !isThis && onOpenAppointment !== undefined ? (
                      <button
                        type="button"
                        className={styles.chainOpen}
                        onClick={() => {
                          const booking = part.booking;
                          if (booking !== null) {
                            onOpenAppointment(booking.appointmentId, booking.startsAt);
                          }
                        }}
                        data-testid="panel-chain-open"
                      >
                        <span>{label}</span>
                        <span className={styles.chainWhen}>{when}</span>
                      </button>
                    ) : (
                      <span className={styles.chainRow} data-testid="panel-chain-row">
                        <span>
                          {label}
                          {isThis && <span className={styles.chainMark}> · this one</span>}
                        </span>
                        <span className={styles.chainWhen}>{when}</span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {edits && (
            <button
              type="button"
              className={styles.detach}
              disabled={busy}
              onClick={() => void detach()}
              data-testid="panel-plan-detach"
            >
              Not this visit
            </button>
          )}
        </div>
      )}

      {error !== null && (
        <p className={styles.error} role="alert" data-testid="panel-plan-error">
          {error}
        </p>
      )}
    </section>
  );
}

/** `Thu 24 Sep, 10:00`, on the branch's clock. */
function formatWhen(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}
